/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/pdfInvoiceTableSettings.scss';

import React, {PureComponent} from 'react';
import PdfInvoiceTable from "../../invoiceTable/pdfInvoiceTable";
import {debug} from "../../../util/miscUtil";
import {ElementPosition} from "../../../util/interfaces";
import {fontItems} from "../../../util/pdfUtil";
import {ColorPicker, DvrdNumberInput, DvrdSelect} from "../../../../../index";
import IconButton, {IconButtonType} from "../buttons/iconButton";

interface Props {
    element: PdfInvoiceTable;
    headColorEnabled?: boolean;
}

interface State {
    widths: number[];
    color: string;
    headerColor: string;
    colorPickerActive: boolean;
    headColorPickerActive: boolean;
    alignment?: ElementPosition;
}

const TITLES = ['Omschrijving', 'Aantal', 'Prijs', 'BTW', 'Subtotaal'];

export default class PdfInvoiceTableSettings extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        const settings = props.element.getSettings();
        this.state = {
            widths: settings.widths,
            color: settings.color,
            headerColor: settings.headerColor,
            colorPickerActive: false,
            headColorPickerActive: false,
            alignment: settings.alignment,
        };
    }

    onCloseColorPicker = () => {
        this.setState({colorPickerActive: false});
    };

    onOpenColorPicker = () => {
        this.setState({colorPickerActive: true});
    };

    onPickColor = (close: boolean = true) => (color: string) => {
        const {element} = this.props;
        if (close)
            this.setState({colorPickerActive: false, color}, () => {
                element.setSetting('color', color)
            });
        else this.setState({color}, () => {
            element.setSetting('color', color, undefined, false);
        });
    };

    onCloseHeadColorPicker = () => {
        this.setState({headColorPickerActive: false});
    };

    onOpenHeadColorPicker = () => {
        this.setState({headColorPickerActive: true});
    };

    onPickHeadColor = (close: boolean = true) => (color: string) => {
        const {element} = this.props;
        if (close)
            this.setState({headColorPickerActive: false, headerColor: color}, () => {
                element.setSetting('headerColor', color)
            });
        else this.setState({headerColor: color}, () => {
            element.setSetting('headerColor', color, undefined, false);
        });
    };

    onChangeWidth = (index: number) => (value: number) => {
        const {element} = this.props, widths = element.getSettings().widths.slice(),
            remainder = this.getRemainingWidth(index);
        debug('Remainder', {remainder});
        if (value > remainder) value = remainder;
        widths[index] = value;
        element.setSetting('widths', widths);
        this.setState({widths});
    };

    onToggleAlignment = (type: IconButtonType) => () => {
        const {alignment} = this.state, {element} = this.props;
        let nextAlignment: ElementPosition | undefined;
        if (type === IconButtonType.LEFT)
            nextAlignment = alignment === ElementPosition.LEFT ? undefined : ElementPosition.LEFT;
        else if (type === IconButtonType.CENTER)
            nextAlignment = alignment === ElementPosition.CENTER ? undefined : ElementPosition.CENTER;
        else if (type === IconButtonType.RIGHT)
            nextAlignment = alignment === ElementPosition.RIGHT ? undefined : ElementPosition.RIGHT;
        element.setSetting('alignment', nextAlignment, () => {
            this.setState({alignment: nextAlignment});
        });
    }

    getRemainingWidth = (excludeIndex: number): number => {
        const {widths} = this.state;
        let remainder: number = 100;
        for (let i = 0; i < widths.length; i++) {
            if (i === excludeIndex) continue;
            remainder -= widths[i];
        }
        return remainder;
    }

    renderWidth = (value: number, index: number) => {
        const title = TITLES[index];
        return (
            <DvrdNumberInput key={index} value={value.toString()} onChange={this.onChangeWidth(index)} label={title}
                         ornaments={{
                             element: <span style={{cursor: 'text'}}>%</span>,
                             placement: ElementPosition.RIGHT,
                             allowPropagation: true
                         }} asNumber unControlled/>
        );
    };

    render = () => {
        const {widths, colorPickerActive, color, alignment, headerColor, headColorPickerActive} = this.state;
        const {element, headColorEnabled} = this.props;
        const settings = element.getSettings();
        return (
            <div className='pdf-invoice-table-settings'>
                <label className='settings-label'>Kolom breedtes</label>
                <div className='center-btn'>
                    <IconButton onClick={this.onToggleAlignment} active={alignment === ElementPosition.LEFT}
                                type={IconButtonType.LEFT} title='Links'/>
                    <IconButton onClick={this.onToggleAlignment} active={alignment === ElementPosition.CENTER}
                                type={IconButtonType.CENTER} title='Centreren'/>
                    <IconButton onClick={this.onToggleAlignment} active={alignment === ElementPosition.RIGHT}
                                type={IconButtonType.RIGHT} title='Rechts'/>
                </div>
                {widths.map(this.renderWidth)}
                <div className='divider'/>
                <label className='settings-label'>Tekstgrootte</label>
                <DvrdSelect items={fontItems} value={settings.font} onChange={element.changeSetting('font')}
                            label='Lettertype' optionsContainerHeight='15rem' unControlled/>
                <DvrdNumberInput value={settings.fontSize} onChange={element.changeSetting('fontSize')}
                                 asNumber
                                 ornaments={{
                                     element: <span style={{cursor: 'text'}}>px</span>,
                                     placement: ElementPosition.RIGHT,
                                     allowPropagation: true
                                 }} wholeNumbers unControlled/>
                <div className='settings-color-picker'>
                    <label className='picker-label'>Tekstkleur:</label>
                    <div className='color-preview' style={{backgroundColor: color}}
                         onClick={this.onOpenColorPicker}/>
                    <ColorPicker onClose={this.onCloseColorPicker} onSubmit={this.onPickColor()} closeable={false}
                                 onPreChange={this.onPickColor(false)} color={settings.color}
                                 visible={colorPickerActive} className='text-color-picker' transparent/>
                </div>
                {!!headColorEnabled && <div className='settings-color-picker'>
                    <label className='picker-label'>Kleur kopregel:</label>
                    <div className='color-preview' style={{backgroundColor: headerColor}}
                         onClick={this.onOpenHeadColorPicker}/>
                    <ColorPicker onClose={this.onCloseHeadColorPicker} onSubmit={this.onPickHeadColor()}
                                 closeable={false} onPreChange={this.onPickHeadColor(false)}
                                 color={settings.headerColor} visible={headColorPickerActive}
                                 className='text-color-picker' transparent/>
                </div>}
            </div>
        )
    }
}